import RequirePricingAdminUser from '@/components/auth/RequirePricingAdminUser';
import DateTimeDisplay from '@/components/DateTime';
import {
  Badge,
  Box,
  Button,
  Card,
  Column,
  Table,
  toast,
  useBreakpoint,
  useOpenClose,
} from '@palmetto/palmetto-components';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { PriceSheetStatus } from 'types';

import {
  useDeleteOrganizationPpwModifierMutation,
  useGetOrganizationPpwModifiersQuery,
} from '../../../../../services/pricing';
import { TableLoading } from '../../../../TableLoading';

import { CreatePpwModifier } from './modals/CreatePpwModifier';

export const PpwModifiers = () => {
  const { alias = '', programType = '' } = useParams<{ alias: string; programType: string }>();
  const { isPhone } = useBreakpoint();
  const { isOpen: isModalOpen, handleOpen: openModal, handleClose: closeModal } = useOpenClose();
  const { data, isLoading, isFetching } = useGetOrganizationPpwModifiersQuery(
    { alias, programType },
    { skip: !alias || !programType },
  );
  const [deleteOrganizationPpwModifierMutation] = useDeleteOrganizationPpwModifierMutation();

  const ppwModifiers = useMemo(() => {
    if (!data || !data?.ppwModifiers?.length) {
      return [];
    }
    const modifiersByStatus = data.ppwModifiers.reduce((acc: any, modifier: any) => {
      acc[modifier.status] = acc[modifier.status] || [];
      acc[modifier.status].push(modifier);
      return acc;
    }, {});
    const sortByCreatedDateDesc = (a: any, b: any) =>
      new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime();

    return [
      ...(modifiersByStatus[PriceSheetStatus.active] || []).toSorted(sortByCreatedDateDesc),
      ...(modifiersByStatus[PriceSheetStatus.archived] || []).toSorted(sortByCreatedDateDesc),
    ];
  }, [data]);
  const handleArchive = async (id: string) => {
    try {
      await deleteOrganizationPpwModifierMutation({ alias, id, programType }).unwrap();
      toast.success('PPW Modifier Archived successfully');
    } catch (e) {
      console.error(e);
      toast.error('Error archiving PPW Modifier');
    }
  };

  const columnConfig: Column[] = [
    {
      heading: 'status',
      dataKey: 'status',
      render: (cell: any) => (
        <Badge
          variant={
            cell === PriceSheetStatus.active ? 'success' : cell === PriceSheetStatus.archived ? 'warning' : 'danger'
          }
          message={cell}
        />
      ),
    },
    { heading: 'Name', dataKey: 'name' },
    { heading: 'org', dataKey: 'organizationName' },
    { heading: 'State', dataKey: 'state' },
    { heading: 'Modifier Amount', dataKey: 'amount' },
    {
      heading: 'Created',
      dataKey: 'createdDate',
      width: 150,
      render: (_cell: any, row: any) => <DateTimeDisplay value={row?.createdDate} dateFormat={'DD'} excludeTime />,
    },
    {
      heading: 'Archived',
      dataKey: 'archivedDate',
      width: 150,
      render: (_cell: any, row: any) => <DateTimeDisplay value={row?.archivedDate} dateFormat={'DD'} excludeTime />,
    },
    {
      heading: '',
      render: (_cell: any, row: any) => {
        return (
          row.status === PriceSheetStatus.active && (
            <RequirePricingAdminUser>
              <Button
                size="sm"
                variant="secondary"
                tone="neutral"
                onClick={() => {
                  handleArchive(row.id);
                }}
              >
                Archive
              </Button>
            </RequirePricingAdminUser>
          )
        );
      },
    },
  ];
  return (
    <>
      <CreatePpwModifier isModalOpen={isModalOpen} closeModal={closeModal} />
      <Card.Header>
        <RequirePricingAdminUser>
          <Box direction="row" justifyContent="space-between">
            <Box />
            <Button
              as="button"
              variant="primary"
              iconPrefix="add"
              size={{ base: 'sm', desktop: 'md' }}
              aria-label="create adder"
              onClick={() => openModal()}
            >
              {!isPhone && 'PPW Modifier'}
            </Button>
          </Box>
        </RequirePricingAdminUser>
      </Card.Header>
      <Box margin="0 lg 0 lg">
        {isFetching || isLoading ? (
          <TableLoading rows={12} />
        ) : (
          <Table
            rowKey="id"
            columns={columnConfig}
            rows={ppwModifiers ?? []}
            isLoading={isLoading || isFetching}
            isScrollable={{ y: true }}
            hasStickyHeader
          />
        )}
      </Box>
    </>
  );
};
